import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Badge,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  FormGroup,
  Label,
  Spinner,
} from "reactstrap";
import { withRouter } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  getActivityLogs,
  getActivityLogById,
  getActivityLogFilters,
} from "../../Endpoint/Admin";

class ActivityLogList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      logs: [],
      pagination: { page: 1, limit: 20, total: 0, pages: 1 },
      loading: true,
      filterOptions: { modules: [], actions: [], users: [] },
      filters: {
        userId: "",
        module: "",
        action: "",
        startDate: "",
        endDate: "",
        search: "",
      },
      selectedLog: null,
      detailModal: false,
      breadcrumbItems: [
        { title: "Lubumbashi", link: "#" },
        { title: "Activity Logs", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadFilters();
    this.loadLogs();
  }

  loadFilters = async () => {
    const response = await getActivityLogFilters();
    if (response && response.success) {
      this.setState({ filterOptions: response.data });
    }
  };

  loadLogs = async (page = 1) => {
    this.setState({ loading: true });
    const { filters, pagination } = this.state;

    const params = {
      page,
      limit: pagination.limit,
      ...Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== "")
      ),
    };

    const response = await getActivityLogs(params);
    if (response && response.success) {
      this.setState({
        logs: response.data,
        pagination: response.pagination,
        loading: false,
      });
    } else {
      this.setState({ loading: false });
    }
  };

  handleFilterChange = (e) => {
    const { name, value } = e.target;
    this.setState(
      (prevState) => ({
        filters: { ...prevState.filters, [name]: value },
      }),
      () => {
        if (name !== "search") {
          this.loadLogs(1);
        }
      }
    );
  };

  handleSearchSubmit = (e) => {
    e.preventDefault();
    this.loadLogs(1);
  };

  clearFilters = () => {
    this.setState(
      {
        filters: {
          userId: "",
          module: "",
          action: "",
          startDate: "",
          endDate: "",
          search: "",
        },
      },
      () => this.loadLogs(1)
    );
  };

  viewLogDetail = async (log) => {
    const response = await getActivityLogById(log._id);
    if (response && response.success) {
      this.setState({ selectedLog: response.data, detailModal: true });
    }
  };

  toggleDetailModal = () => {
    this.setState((prevState) => ({
      detailModal: !prevState.detailModal,
      selectedLog: prevState.detailModal ? null : prevState.selectedLog,
    }));
  };

  getActionBadge = (action) => {
    const colors = {
      CREATE: "success",
      UPDATE: "info",
      DELETE: "danger",
      STATUS_CHANGE: "warning",
      TOGGLE: "secondary",
      LOGIN: "primary",
      LOGOUT: "dark",
      ASSIGN: "info",
      EXPORT: "secondary",
    };
    return <Badge color={colors[action] || "secondary"}>{action}</Badge>;
  };

  getModuleBadge = (module) => {
    const colors = {
      TEAM: "primary",
      COUPON: "success",
      BANNER: "info",
      ROOM: "warning",
      FOOD: "danger",
      TRANSPORT: "dark",
      AUTH: "secondary",
    };
    return (
      <Badge color={colors[module] || "secondary"} className="badge-soft">
        {module}
      </Badge>
    );
  };

  formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  renderPagination = () => {
    const { pagination } = this.state;
    const { page, pages } = pagination;

    if (pages <= 1) return null;

    const pageNumbers = [];
    const maxPages = 5;
    let startPage = Math.max(1, page - Math.floor(maxPages / 2));
    let endPage = Math.min(pages, startPage + maxPages - 1);

    if (endPage - startPage + 1 < maxPages) {
      startPage = Math.max(1, endPage - maxPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <nav>
        <ul className="pagination justify-content-center">
          <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => this.loadLogs(page - 1)}
              disabled={page === 1}
            >
              Previous
            </button>
          </li>
          {pageNumbers.map((num) => (
            <li
              key={num}
              className={`page-item ${page === num ? "active" : ""}`}
            >
              <button className="page-link" onClick={() => this.loadLogs(num)}>
                {num}
              </button>
            </li>
          ))}
          <li className={`page-item ${page === pages ? "disabled" : ""}`}>
            <button
              className="page-link"
              onClick={() => this.loadLogs(page + 1)}
              disabled={page === pages}
            >
              Next
            </button>
          </li>
        </ul>
      </nav>
    );
  };

  renderChanges = (changes) => {
    if (!changes || (!changes.before && !changes.after)) return null;

    return (
      <div className="mt-3">
        <h6>Changes</h6>
        <Row>
          {changes.before && (
            <Col md={6}>
              <Card className="bg-light">
                <CardBody className="p-2">
                  <small className="text-muted">Before</small>
                  <pre className="mb-0" style={{ fontSize: "12px" }}>
                    {JSON.stringify(changes.before, null, 2)}
                  </pre>
                </CardBody>
              </Card>
            </Col>
          )}
          {changes.after && (
            <Col md={6}>
              <Card className="bg-light">
                <CardBody className="p-2">
                  <small className="text-muted">After</small>
                  <pre className="mb-0" style={{ fontSize: "12px" }}>
                    {JSON.stringify(changes.after, null, 2)}
                  </pre>
                </CardBody>
              </Card>
            </Col>
          )}
        </Row>
      </div>
    );
  };

  render() {
    const {
      logs,
      loading,
      filters,
      filterOptions,
      pagination,
      selectedLog,
      detailModal,
    } = this.state;

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs
              title="Activity Logs"
              breadcrumbItems={this.state.breadcrumbItems}
            />

            {/* Filters */}
            <Card>
              <CardBody>
                <Row>
                  <Col md={2}>
                    <FormGroup>
                      <Label>Module</Label>
                      <Input
                        type="select"
                        name="module"
                        value={filters.module}
                        onChange={this.handleFilterChange}
                      >
                        <option value="">All Modules</option>
                        {filterOptions.modules.map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label>Action</Label>
                      <Input
                        type="select"
                        name="action"
                        value={filters.action}
                        onChange={this.handleFilterChange}
                      >
                        <option value="">All Actions</option>
                        {filterOptions.actions.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label>User</Label>
                      <Input
                        type="select"
                        name="userId"
                        value={filters.userId}
                        onChange={this.handleFilterChange}
                      >
                        <option value="">All Users</option>
                        {filterOptions.users.map((u) => (
                          <option key={u.id} value={u.id}>
                            {u.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        name="startDate"
                        value={filters.startDate}
                        onChange={this.handleFilterChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        name="endDate"
                        value={filters.endDate}
                        onChange={this.handleFilterChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <FormGroup>
                      <Label>Search</Label>
                      <form onSubmit={this.handleSearchSubmit}>
                        <Input
                          type="text"
                          name="search"
                          placeholder="Search..."
                          value={filters.search}
                          onChange={this.handleFilterChange}
                        />
                      </form>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={this.clearFilters}
                    >
                      Clear Filters
                    </Button>
                  </Col>
                  <Col className="text-right">
                    <small className="text-muted">
                      Total: {pagination.total} logs
                    </small>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Logs Table */}
            <Card>
              <CardBody>
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                    <p className="mt-2">Loading activity logs...</p>
                  </div>
                ) : logs.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="ri-file-list-3-line" style={{ fontSize: "48px", color: "#ccc" }}></i>
                    <p className="text-muted mt-2">No activity logs found</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <Table className="table-hover mb-0">
                        <thead className="thead-light">
                          <tr>
                            <th>Timestamp</th>
                            <th>User</th>
                            <th>Module</th>
                            <th>Action</th>
                            <th>Description</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map((log) => (
                            <tr key={log._id}>
                              <td>
                                <small>{this.formatDate(log.createdAt)}</small>
                              </td>
                              <td>
                                <strong>{log.userName}</strong>
                                <br />
                                <small className="text-muted">
                                  {log.userEmail}
                                </small>
                              </td>
                              <td>{this.getModuleBadge(log.module)}</td>
                              <td>{this.getActionBadge(log.action)}</td>
                              <td>
                                <span
                                  style={{
                                    maxWidth: "300px",
                                    display: "inline-block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {log.description}
                                </span>
                              </td>
                              <td>
                                <Button
                                  color="link"
                                  size="sm"
                                  onClick={() => this.viewLogDetail(log)}
                                >
                                  <i className="ri-eye-line"></i> View
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    <div className="mt-4">{this.renderPagination()}</div>
                  </>
                )}
              </CardBody>
            </Card>
          </Container>
        </div>

        {/* Detail Modal */}
        <Modal isOpen={detailModal} toggle={this.toggleDetailModal} size="lg">
          <ModalHeader toggle={this.toggleDetailModal}>
            Activity Log Detail
          </ModalHeader>
          <ModalBody>
            {selectedLog && (
              <div>
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>User:</strong> {selectedLog.userName}
                    </p>
                    <p>
                      <strong>Email:</strong> {selectedLog.userEmail}
                    </p>
                    <p>
                      <strong>Module:</strong>{" "}
                      {this.getModuleBadge(selectedLog.module)}
                    </p>
                    <p>
                      <strong>Action:</strong>{" "}
                      {this.getActionBadge(selectedLog.action)}
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Timestamp:</strong>{" "}
                      {this.formatDate(selectedLog.createdAt)}
                    </p>
                    <p>
                      <strong>Entity Type:</strong> {selectedLog.entityType}
                    </p>
                    <p>
                      <strong>Entity Name:</strong>{" "}
                      {selectedLog.entityName || "N/A"}
                    </p>
                    <p>
                      <strong>IP Address:</strong>{" "}
                      {selectedLog.ipAddress || "N/A"}
                    </p>
                  </Col>
                </Row>
                <hr />
                <p>
                  <strong>Description:</strong>
                </p>
                <p className="bg-light p-2 rounded">
                  {selectedLog.description}
                </p>
                {this.renderChanges(selectedLog.changes)}
                {selectedLog.metadata &&
                  Object.keys(selectedLog.metadata).length > 0 && (
                    <div className="mt-3">
                      <h6>Metadata</h6>
                      <pre
                        className="bg-light p-2 rounded"
                        style={{ fontSize: "12px" }}
                      >
                        {JSON.stringify(selectedLog.metadata, null, 2)}
                      </pre>
                    </div>
                  )}
              </div>
            )}
          </ModalBody>
        </Modal>
      </React.Fragment>
    );
  }
}

export default withRouter(withTranslation()(ActivityLogList));
